import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { COOKIES, Cookies } from '../../server.tokens';

export const REDIRECT_COOKIE_NAME = 'authorize_redirect';
export const CULTURE_COOKIE_NAME = 'culture';

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private platformId = inject<Platform>(PLATFORM_ID);
  private cookies = inject<string>(COOKIES, { optional: true });

  private cookieMap: Cookies;

  constructor() {
    const cookies = this.cookies;
    if (isPlatformServer(this.platformId) && cookies) {
      this.cookieMap = this.parseCookies(cookies);
    } else if (isPlatformBrowser(this.platformId)) {
      this.cookieMap = this.parseCookies(document.cookie);
    } else {
      this.cookieMap = {};
    }
  }

  private parseCookies(cookieString: string): Cookies {
    const cookies: Cookies = {};
    if (cookieString) {
      cookieString.split(';').forEach((cookie) => {
        const [key, value] = cookie.split('=');
        const cookieKey = key.trim();
        cookies[cookieKey] = decodeURIComponent(value.trim());
      });
    }
    return cookies;
  }

  setCookie(name: string, value: string, expiryDays?: number): void {
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    if (expiryDays) {
      const date = new Date();
      date.setTime(date.getTime() + expiryDays * 24 * 60 * 60 * 1000);
      const expires = date.toUTCString();
      cookieString += `; expires=${expires}`;
    }
    if (name === REDIRECT_COOKIE_NAME) {
      cookieString += '; Secure';
    }
    cookieString += '; path=/';
    this.cookieMap[name] = value; // Update the local cookie map

    if (isPlatformBrowser(this.platformId)) {
      document.cookie = cookieString; // Set the cookie in the document
    }
  }

  getCookie(name: string): string | undefined {
    if (isPlatformBrowser(this.platformId)) {
      // Client-side cookies handling
      this.cookieMap = this.parseCookies(document.cookie);
    } else if (isPlatformServer(this.platformId) && this.cookies) {
      // Server-side cookies handling
      this.cookieMap = this.parseCookies(this.cookies);
    }
    return this.cookieMap[name] || undefined;
  }

  removeCookie(name: string): void {
    if (isPlatformBrowser(this.platformId)) {
      document.cookie =
        name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
    }
    delete this.cookieMap[name];
  }
}
